import toast from '@/utils/toast'

export interface FileValidationResult {
  valid: boolean
  message?: string
}

export const validateFileTypes = (selected: File[], accept: string) => {
  const acceptedTypes = accept.split(',').map(type => type.trim())
  const invalidFiles = selected.filter(file => {
    return !acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      }
      return file.type === type
    })
  })

  if (!invalidFiles.length) return { valid: true }

  return {
    valid: false,
    message: `File(s) '${invalidFiles.map(f => f.name).join(', ')}' are not an accepted file type.`
  }
}

export const validateDuplicates = (selected: File[], existing: File[]) => {
  const duplicates = selected.filter(f => existing.some(e => e.name === f.name))
  if (!duplicates.length) return { valid: true }

  return {
    valid: false,
    message: `File(s) '${duplicates.map(d => d.name).join(', ')}' are already selected.`
  }
}

export const validateMaxSize = (selected: File[], maxMB: number) => {
  const maxBytes = maxMB * 1024 * 1024
  const oversized = selected.filter(f => f.size > maxBytes)
  if (!oversized.length) return { valid: true }

  return {
    valid: false,
    message: `File(s) '${oversized.map(f => f.name).join(', ')}' exceed ${maxMB}MB.`
  }
}

export const validateMaxFiles = (selected: File[], current: number, max: number) => {
  if (current + selected.length <= max) return { valid: true }

  return {
    valid: false,
    message: 'You have reached the maximum file limit.'
  }
}

export const validateImageResolution = async (
  selected: File[],
  minWidth?: number,
  minHeight?: number
) => {
  const imageFiles = selected.filter(f => f.type.startsWith('image/'))
  if (!imageFiles.length || (!minWidth && !minHeight))
    return { valid: true }

  const failures: string[] = []

  await Promise.all(imageFiles.map(file => (
    new Promise<void>(resolve => {
      const img = new Image()
      img.onload = () => {
        if ((minWidth && img.width < minWidth) || (minHeight && img.height < minHeight)) {
          failures.push(file.name)
        }
        resolve()
      }
      img.onerror = () => {
        failures.push(file.name)
        resolve()
      }
      img.src = URL.createObjectURL(file)
    })
  )))

  if (!failures.length) return { valid: true }

  return {
    valid: false,
    message: `Image(s) '${failures.join(', ')}' do not meet the minimum resolution of ${minWidth || 0}x${minHeight || 0}px.`
  }
}

export const rejectInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  message: string
) => {
  toast.error(message)
  e.target.value = ''
}
